import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { isCurrentlyOnSecondaryTeam, User } from 'entities';

import { Column, Row, Button, LabeledSelect, ResponsiveOverlay, Alert } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { FromToArrows } from 'components/FromToArrows';
import { getUserLabel } from 'components/Inputs';
import { UserPenIcon, XIcon, CircleXIcon } from 'components/Icons';

import { useUser } from 'hooks/user';
import { useTeam } from 'providers/Team';
import { useJustification } from 'providers/Justification';

import { sortByName } from 'utils/array';
import { getFirstAndLastName } from 'utils/text';
import { emptyFn } from 'utils/function';

import { OpenModalParams, ownershipChangeCtx } from './context';

interface Props {
  children: ReactNode;
}

type ModalState = OpenModalParams & {
  isOpen: boolean;
};

const baseNoUser = { email: 'a', picture: '/a.jpg', name: '?' } as User;

export function ChangeOwnerModalProvider({ children }: Props) {
  const { t } = useLang();
  const userData = useUser();

  const { teamUsers, loadingUsers } = useTeam();

  const [modalConfig, setModalConfig] = useState<ModalState>({ isOpen: false } as ModalState);

  const [newOwner, setNewOwner] = useState('');

  const { openJustificationModal } = useJustification();

  const closeModal = useCallback(() => {
    setNewOwner('');

    setModalConfig((old) => ({
      ...old,
      isOpen: false,
      onConfirm: emptyFn,
      cancel: emptyFn,
    }));
  }, []);

  const handleCloseModal = useCallback(() => {
    modalConfig?.cancel?.();

    closeModal();
  }, [modalConfig, closeModal]);

  const openChangeOwnerModal = useCallback(
    ({ requireJustification = true, ...config }: OpenModalParams): void =>
      setModalConfig({ ...config, requireJustification, isOpen: true }),
    [],
  );

  const selectedNewOwner = useMemo(() => {
    return teamUsers.find((user) => user.id === newOwner);
  }, [newOwner, teamUsers]);

  const newOwnerOptions = useMemo(() => {
    return sortByName(
      teamUsers.filter((user) =>
        [
          user.id !== modalConfig?.currentOwner?.id,
          user.id !== userData.id ? true : userData.type === 'LEADER',
        ].every(Boolean),
      ),
    );
  }, [userData, teamUsers, modalConfig]);

  const handleConfirmClick = useCallback(() => {
    const caller = async (justification: string): Promise<void> => {
      modalConfig?.onConfirm(selectedNewOwner as User, justification);

      closeModal();
    };

    if (!modalConfig?.requireJustification) return caller('');

    openJustificationModal({
      text: t('please-provide-detail-explanation-as-to-why-this-change-is-being-made'),

      charLimit: 500,

      cancel: modalConfig?.cancel as () => void,

      onConfirm: caller,
    });
  }, [modalConfig, selectedNewOwner, openJustificationModal, t, closeModal]);

  const handleRemovalClick = useCallback(() => {
    openJustificationModal({
      text: t('please-provide-detail-explanation-as-to-why-this-change-is-being-made'),

      cancel: modalConfig?.cancel as () => void,

      onConfirm: async (justification) => {
        modalConfig?.onConfirm(null, justification);

        closeModal();
      },
    });
  }, [modalConfig, openJustificationModal, t, closeModal]);

  const data = useMemo(
    () => ({
      openChangeOwnerModal,
    }),
    [openChangeOwnerModal],
  );
  return (
    <ownershipChangeCtx.Provider value={data}>
      <>{children}</>

      <ResponsiveOverlay
        isOpen={modalConfig.isOpen}
        close={handleCloseModal}
        title={modalConfig?.text || (t('change-owner-?') as string)}
        width="min(90vw, 860px)"
      >
        {modalConfig && (
          <Row fullWidth justifyContent="center" gap="12" className="phone:gap-4">
            <Column gap="1" alignItems="center">
              <UserAvatar user={modalConfig.currentOwner || baseNoUser} />

              <p className="whitespace-nowrap text-sm font-medium">
                {getFirstAndLastName(modalConfig?.currentOwner?.name || '')}
              </p>
            </Column>

            <div className="small_phone:hidden">
              <FromToArrows />
            </div>

            <Column gap="1" alignItems="center">
              <UserAvatar user={selectedNewOwner || baseNoUser} />

              <p className="whitespace-nowrap text-sm font-medium">
                {getFirstAndLastName(selectedNewOwner?.name || '') || t('choose-new-user')}
              </p>
            </Column>
          </Row>
        )}

        <LabeledSelect
          options={newOwnerOptions}
          value={newOwner}
          onChangeDirect={setNewOwner}
          label={t('new-owner')}
          loading={loadingUsers}
          labelKey="name"
          formatLabel={getUserLabel}
        />

        {isCurrentlyOnSecondaryTeam(userData) ? (
          <Alert title={t('you-are-on-2nd-team')}>
            {t('please-make-sure-it-is-correct-to-make-this-change')}
          </Alert>
        ) : (
          <p className="w-full text-center">
            {t('please-make-sure-it-is-correct-to-make-this-change')}
          </p>
        )}

        <Row justifyContent="space-between" className="phone:flex-col phone:items-stretch">
          <Button className="min-w-[140px]" variant="outlined" onClick={handleCloseModal}>
            <XIcon />
            {t('cancel')}
          </Button>

          {modalConfig?.allowRemoval && modalConfig?.currentOwner && (
            <Button
              className="min-w-[140px]"
              onClick={handleRemovalClick}
              variant="outlined"
              color="destructive"
            >
              <CircleXIcon />
              {t('remove')}
            </Button>
          )}

          <Button className="min-w-[140px]" disabled={!newOwner} onClick={handleConfirmClick}>
            <UserPenIcon />
            {modalConfig.buttonText ?? t('change-owner')}
          </Button>
        </Row>
      </ResponsiveOverlay>
    </ownershipChangeCtx.Provider>
  );
}
